<template>
    <div class="w-full pb-28 md:pb-0">
        <div class="flex flex-col items-start justify-start gap-2 w-full pb-8 md:pb-6">
            <h1 class="self-stretch text-[24px] leading-8 md:text-[30px] md:leading-9 font-semibold text-slate-800">
                Where do you dream of going?
            </h1>
            <p class="self-stretch text-slate-500 text-base leading-6">
                Add up to 3 dream destinations. This lets us tailor flight and rewards recommendations for you.
            </p>
        </div>
        <div class="flex flex-col items-start gap-4 w-full">
            <div
                v-for="i in Math.max(1, destinationInputs)"
                :key="i"
                class="w-full flex items-center gap-2"
            >
                <div class="flex-1">
                    <template v-if="hasGoogle">
                        <Suspense>
                            <template #default>
                                <PlacesAutocomplete
                                    inputClass="py-2.5 px-4"
                                    :hideClear="false"
                                    :key="'dest-input-' + i + '-' + destRenderKey"
                                    :types="['(regions)']"
                                    :id="'onb-destination-' + i"
                                    placeholder="City or country"
                                    @placeChanged="(p) => onDestPlaceChanged(i - 1, p)"
                                    @placeCleared="() => onDestPlaceCleared(i - 1)"
                                    @onFocus="markTouched(i - 1)"
                                    :error="attemptSubmit && destTouched[i - 1] && !destFields[i - 1]?.place"
                                    :currentPlace="destFields[i - 1]?.place || null"
                                />
                            </template>
                            <template #fallback>
                                <input
                                    :id="'onb-destination-fallback-loading-' + i"
                                    type="text"
                                    placeholder="City or country"
                                    class="w-full px-4 py-2.5 rounded border border-slate-200 bg-white text-slate-800 placeholder-slate-400"
                                />
                            </template>
                        </Suspense>
                    </template>
                    <template v-else>
                        <div class="relative">
                            <input
                                :id="'onb-destination-fallback-' + i"
                                type="text"
                                v-model="destDrafts[i - 1]"
                                placeholder="City or country"
                                class="w-full px-4 py-2.5 rounded border bg-white text-slate-800 placeholder-slate-400"
                                :class="attemptSubmit && destTouched[i - 1] && !destDrafts[i - 1] ? 'border-red-500' : 'border-slate-200'"
                                @keydown.enter.prevent="addDestinationText(i - 1)"
                            />
                            <button
                                v-if="destFields[i - 1]?.place || destDrafts[i - 1]"
                                type="button"
                                class="absolute right-2 top-1/2 transform -translate-y-1/2 z-10"
                                @click="clearDest(i - 1)"
                                aria-label="Clear destination"
                            >
                                <Icon icon="close" class="text-slate-500" />
                            </button>
                        </div>
                    </template>
                    <div v-if="i === 1 && errors.length > 0">
                        <Errors :errors="errors" />
                    </div>
                </div>
            </div>

            <button
                v-if="destinationInputs < 3"
                type="button"
                class="inline-flex items-center gap-2 text-slate-800 font-medium"
                @click="destinationInputs++"
            >
                <Icon icon="plus" />
                <span>Add destination</span>
            </button>
        </div>

        <QuizActionBar
            primaryLabel="Continue"
            secondaryLabel="Skip"
            :disabled="saving"
            @primary="saveDestinations"
            @secondary="skipDestinations"
        />
    </div>
</template>

<script setup>
import Icon from '@/components/ui/icon/icon.vue';
import Errors from '@/components/errors.vue';
import PlacesAutocomplete from '@/components/ui/places-autocomplete/places-autocomplete.vue';
import QuizActionBar from './QuizActionBar.vue';
import { useQuizWizard } from '@/composables/useQuizWizard';

const {
    saving, errors,
    destinationInputs, destRenderKey, hasGoogle, attemptSubmit,
    destFields, destTouched, destDrafts,
    onDestPlaceChanged, onDestPlaceCleared, markTouched, addDestinationText, clearDest,
    saveDestinations, skipDestinations,
} = useQuizWizard();
</script>
